// ============================================================
// DebugConsole — in-player debug overlay (React component)
//
// Rendered by DebugPlugin when registered into the 'debug-console'
// UIRegistry slot. Provides:
//   • Live Stats panel (full-width grid) — engine, quality, buffer,
//     bandwidth, latency, LL-HLS mode, viewers, CDN
//   • Scrollable timestamped critical-event log (small strip below stats)
//   • Command input: only 'clear' is available
//
// Toggle visibility: hotkey ` / Backquote (emits 'debug:toggle' via Hotkeys.ts)
// ============================================================
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { usePlayerContext } from '../../ui-react/PlayerContext';
import { useStore } from '../../ui-react/hooks/useStore';
import { useEventBus } from '../../ui-react/hooks/useEventBus';
import styles from './DebugConsole.module.css';

const TRIM_AT = 50;
const KEEP_LINES = 20;

function formatStateValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return JSON.stringify(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function DebugConsole() {
  const { store, eventBus } = usePlayerContext();
  const commitHash = typeof __GGPLAYER_COMMIT_HASH__ === 'string'
    ? __GGPLAYER_COMMIT_HASH__
    : 'unknown';

  const [isVisible, setIsVisible] = useState(false);
  const [lines, setLines] = useState<string[]>([]);

  const logPanelRef = useRef<HTMLDivElement | null>(null);

  const handleToggle = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  useEffect(() => {
    store.setState({ isDebugVisible: isVisible });
  }, [isVisible, store]);

  useEventBus(eventBus, 'debug:toggle', handleToggle);

  const handleLog = useCallback((line: string) => {
    setLines(prev => {
      const next = [...prev, line];
      return next.length > TRIM_AT ? next.slice(next.length - KEEP_LINES) : next;
    });
  }, []);

  useEventBus(eventBus, 'debug:log', handleLog);

  useEffect(() => {
    const el = logPanelRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const fullState = useStore(store, useCallback(s => s, []));

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <div
      className={`${styles.overlay}${isVisible ? ` ${styles.visible}` : ''}`}
      data-no-toggle
      onClick={stopPropagation}
      onDoubleClick={stopPropagation}
    >
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.headerTitle}>GGPlayer Debug Console</span>
        <span className={styles.commitHash}>commit {commitHash}</span>
        <span className={styles.keyHint}>Press ` to close</span>
      </div>

      {/* Body: stats (full-width grid, top) + log strip (bottom) */}
      <div className={styles.body}>
        {/* Live Stats — full PlayerState, reactive */}
        <div className={styles.statsPanel}>
          <div className={styles.statsGrid}>
            {Object.entries(fullState).map(([key, value]) => (
              <div key={key} className={styles.statRow}>
                <span className={styles.statLabel}>{key}</span>
                <span className={styles.statValue}>{formatStateValue(value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable log — small strip */}
        <div className={styles.logPanel} ref={logPanelRef}>
          {lines.map((line, i) => (
            <div
              key={i}
              className={[
                styles.logLine,
                line.includes('✖') ? styles.error : '',
                line.startsWith('>') ? styles.highlight : '',
              ].filter(Boolean).join(' ')}
            >
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
